import { router, useForm } from "@inertiajs/react"
import { useEffect, useState, type FormEvent } from "react"

import { useConfirm } from "@/components/confirm-dialog"
import { BaseErrors, MatchFields, ReadOnlyValue } from "@/components/payment_matching_rules/fields"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { requestJson } from "@/lib/http"
import { frontPaymentMatchingRules, frontPaymentsCategoryRuleDrafts, frontPaymentsCounterpartyRuleDrafts } from "@/routes"
import type { PaymentMatchingRuleDraft } from "@/types"

/**
 * Offers a matching rule on any page after a payment got its counterparty
 * (Front::CounterpartyRuleOffer) or the user set its category (Front::PaymentsController#update):
 * the server names the payment in the flash. The user is asked first; the rule is then shown as a
 * draft they can change before creating it.
 */
export function RulePrompt() {
  const confirm = useConfirm()
  const [draft, setDraft] = useState<PaymentMatchingRuleDraft | null>(null)

  useEffect(() => {
    const offer = async (route: { url: string; method: "get" }) => {
      // Null when a rule matches the payment by now.
      const offered = await requestJson<PaymentMatchingRuleDraft | null>(route)
      if (!offered) return

      const subject = offered.match_type === "iban" ? `the IBAN ${offered.match_text}` : `the text "${offered.match_text}"`
      const counterparty = offered.rule_kind === "payment_counterparty"
      const confirmed = await confirm({
        title: counterparty ? `Set up a matching rule for ${offered.target_name}?` : `Set up a rule for the category ${offered.target_name}?`,
        description: `Payments with ${subject} then get this ${counterparty ? "counterparty" : "category"} automatically.`,
        confirmLabel: "Set up rule",
        cancelLabel: "Not now",
      })
      if (confirmed) setDraft(offered)
    }

    return router.on("flash", (event) => {
      const { counterparty_rule_payment_id: counterpartyPaymentId, category_rule_payment_id: categoryPaymentId } = event.detail.flash
      if (counterpartyPaymentId !== undefined) void offer(frontPaymentsCounterpartyRuleDrafts.show(counterpartyPaymentId))
      else if (categoryPaymentId !== undefined) void offer(frontPaymentsCategoryRuleDrafts.show(categoryPaymentId))
    })
  }, [confirm])

  // The key resets the form when another rule is offered.
  return draft && <RuleDialog key={`${draft.rule_kind}:${draft.target_name}:${draft.match_text}`} draft={draft} onClose={() => setDraft(null)} />
}

function RuleDialog({ draft, onClose }: { draft: PaymentMatchingRuleDraft; onClose: () => void }) {
  const form = useForm({
    rule_kind: draft.rule_kind,
    counterparty_id: draft.counterparty_id,
    category: draft.category,
    match_type: draft.match_type,
    match_text: draft.match_text,
  })
  const errors = form.errors as Partial<Record<"match_type" | "match_text" | "normalized_match_text" | "counterparty" | "category" | "base", string[]>>
  const counterparty = draft.rule_kind === "payment_counterparty"

  const submit = (event: FormEvent) => {
    event.preventDefault()
    // The page reloads: the rule also changes payments it already shows.
    form.transform((data) => ({ payment_matching_rule: data, return_to: "back" }))
    form.post(frontPaymentMatchingRules.create().url, { preserveScroll: true, preserveState: true, onSuccess: onClose })
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>New Matching Rule</DialogTitle>
          <DialogDescription>
            Matching payments get this {counterparty ? "counterparty" : "category"} automatically, including imported ones
            that have none.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="grid gap-4">
          <ReadOnlyValue label="Rule target" value={draft.rule_kind_label} />
          <ReadOnlyValue label={counterparty ? "Counterparty" : "Category"} value={draft.target_name} />
          <MatchFields
            idPrefix="offered-rule"
            matchTypes={draft.match_types}
            matchType={form.data.match_type}
            matchTypeErrors={errors.match_type}
            matchText={form.data.match_text}
            // A rule that exists already is reported for the normalized text.
            matchTextErrors={errors.match_text ?? errors.normalized_match_text}
            onMatchType={(matchType) => form.setData("match_type", matchType)}
            onMatchText={(matchText) => form.setData("match_text", matchText)}
          />
          <BaseErrors errors={errors.counterparty ?? errors.category ?? errors.base} />
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={form.processing}>
              Create rule
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
